import { useHistory } from 'react-router';
import { useIsFetching } from '@zg-rentals/react-query5';

import {
  Alert,
  Button,
  Card,
  DialogActions,
  Grid,
  Heading,
  List,
  Paragraph,
  Popover,
  Popper,
  TextButton,
} from '@zillow/constellation';

import { useCreateLeaseRenewalMutation, useLeaseQuery } from '../../../utils/queries';
import LeaseExtension from '../../../models/LeaseExtension';
import { Property } from '../../../models';
import { useLeasingUrlParams } from '../../../hooks/useLeasingUrlParams';

export default function UpdateYourCurrentTermsWithALeaseRenewalAgreement() {
  //const createNewLeaseMutation = useCreateNewLeaseMutation();
  const createLeaseRenewal = useCreateLeaseRenewalMutation();
  const { leaseId, propertyId } = useLeasingUrlParams();
  const history = useHistory();
  const leaseQuery = useLeaseQuery(leaseId);
  const isFetching = useIsFetching() > 0;

  const latestExtension = leaseQuery?.data?.latestExtension
    ? new LeaseExtension(leaseQuery?.data?.latestExtension)
    : null;
  const lease = leaseQuery?.data;

  const endDate = lease?.getLeasePackageFriendlyEndDate();

  // async function onUploadForSigningClick() {
  //   const data = await createNewLeaseMutation.mutateAsync({
  //     propertyId,
  //     leaseType: LeaseTypes.SELF_UPLOAD,
  //   });
  //   // don't use history.push because it won't call on using react-query
  //   window.location.href = Lease.getSelfServePath(data.response.leaseId);
  // }

  // async function onUploadForStorageClick() {
  //   const data = await createNewLeaseMutation.mutateAsync({
  //     propertyId,
  //     leaseType: LeaseTypes.SIGNED_SELF_UPLOAD,
  //   });
  //   window.location.href = Lease.getSelfServePath(data.response.leaseId);
  // }

  async function onCreateLeaseRenewalClick() {
    const response = await createLeaseRenewal.mutateAsync({
      postData: {
        leaseId: Number(leaseId),
      },
    });
    history.push(LeaseExtension.getWizardPath(Number(leaseId), Number(response.leaseExtensionId)));
  }

  function onEditYourCurrentLeaseRenewalClick() {
    history.push(LeaseExtension.getWizardPath(Number(leaseId), Number(latestExtension?.id)));
  }

  function onCreateANewLeaseClick() {
    history.push(Property.getCombinedOnboardingPath(propertyId));
  }

  const isLatestLeaseRenewalNotRatified = latestExtension ? !latestExtension?.isReadOnly() : false;

  const popover = (props, close) => (
    <Popover
      {...props}
      header={
        <Heading level={6} as="h4">
          Lease renewal eligibility
        </Heading>
      }
      body={
        <>
          <Paragraph>Your lease must meet these requirements for renewal:</Paragraph>
          <List>
            <List.Item>It’s a Zillow lease</List.Item>
            <List.Item>It’s currently active or expired</List.Item>
            <List.Item>It has a fixed end date</List.Item>
            <List.Item>It doesn’t already have an existing lease renewal agreement</List.Item>
          </List>
          <DialogActions>
            <TextButton onClick={close}>OK</TextButton>
          </DialogActions>
        </>
      }
    />
  );

  return (
    <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)" gap="sm">
      <Grid gridColumnEnd={{ default: 'span 12', xxl_gte: 'span 8' }}>
        <Heading level={3} as="h2" marginBottom="md">
          Update your current terms with a lease renewal agreement
        </Heading>
        <Paragraph marginBottom="md">
          This agreement is a short amendment to your current lease, sparing you the trouble of creating a whole new
          lease.
          {endDate && latestExtension && (
            <>
              {' '}
              Your lease term ends on <strong>{endDate}</strong>, and the lease renewal will start on the following day.
            </>
          )}
        </Paragraph>
        <Card cardType="secondary" cardElevation={0} padding="md" marginBottom="md">
          {/* <Tag appearance="info" marginBottom="sm">
            Recommended
          </Tag> */}
          {isLatestLeaseRenewalNotRatified && !isFetching && (
            <Alert
              appearance="warning"
              body="You can only have one lease renewal draft at a time. Sign your current draft to finalize it, or delete it in order to create a new lease renewal agreement."
              actionButton={
                <TextButton onClick={onEditYourCurrentLeaseRenewalClick}>Edit your current lease renewal</TextButton>
              }
              marginBottom="sm"
            />
          )}
          {!lease?.isEligibleForLeaseRenewal() && !isLatestLeaseRenewalNotRatified && !isFetching && (
            <Alert
              appearance="warning"
              body={
                <>
                  This lease can’t be renewed
                  <Popper triggered={popover} />
                </>
              }
              actionButton={<TextButton onClick={onCreateANewLeaseClick}>Create new lease</TextButton>}
              marginBottom="sm"
            />
          )}
          <Heading level={5} as="h3" marginBottom="sm">
            Create your own
          </Heading>
          <Paragraph marginBottom="sm">
            Update your terms as needed, then send your renewal for signing — all in only a few minutes
          </Paragraph>
          <Button
            fluid={{ default: true, xl_gte: false }}
            disabled={isLatestLeaseRenewalNotRatified || !lease?.isEligibleForLeaseRenewal()}
            buttonType="primary"
            onClick={onCreateLeaseRenewalClick}
          >
            Create lease renewal
          </Button>
        </Card>
        {/* <Card cardType="secondary" cardElevation={0} padding="md">
          <Heading level={5} as="h3" marginBottom="sm">
            Upload your own
          </Heading>
          <Paragraph marginBottom="sm">
            Upload your own renewal agreement to send for e-signing or storage. To avoid issues, make sure you don’t
            upload a whole new lease.
          </Paragraph>
          <ButtonGroup aria-label="upload options" direction={{ default: 'row', sm_lte: 'column' }}>
            <Button
              icon={<IconSignature />}
              onClick={onUploadForSigningClick}
              fluid={{ default: true, xxl_gte: false }}
              buttonType="secondary"
            >
              Upload for signing
            </Button>
            <Button
              icon={<IconFileOutline />}
              onClick={onUploadForStorageClick}
              fluid={{ default: true, xxl_gte: false }}
              buttonType="secondary"
            >
              Upload for storage
            </Button>
          </ButtonGroup>
        </Card> */}
      </Grid>
    </Grid>
  );
}
